import { forwardRef, useMemo, useRef, useState } from 'react'
import { CommonComboboxProps, ComboboxBase } from '../ComboboxBase'
import { MultipleValue } from '../../Select/types'
import { useFloatingDropdown } from '../useFloatingDropdown'
import { useCombobox } from '../useCombobox'
import clsx from 'clsx'
import styles from '../Combobox.module.scss'
import { Chip } from '../Chip'
import { useMaxItemsDisplayed } from '@/hooks/useMaxItemsDisplayed'

export interface ComboboxMultipleProps extends CommonComboboxProps {
  defaultValue?: MultipleValue
  value?: MultipleValue
  onChange?: (value: MultipleValue) => void
  maxItemsDisplayed?: number
}

export const ComboboxMultiple = forwardRef<HTMLDivElement, ComboboxMultipleProps>(
  (
    {
      placeholder,
      searchPlaceholder,
      children,
      defaultValue,
      defaultIsOpen = false,
      value,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      size = 'md',
      maxItemsDisplayed,
      dropdownMaxHeight,
      showSearch = true,
      ...props
    },
    ref,
  ) => {
    const {
      refs,
      floatingStyles,
      context,
      isOpen,
      elementsRef,
      labelsRef,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
    } = useFloatingDropdown({
      defaultIsOpen,
      disabled,
      dropdownMaxHeight,
    })

    const { selectedIndexes, handleSelect, selectedChildren } = useCombobox({
      defaultValue,
      value,
      onChange,
      children,

      allowMultiple: true,
    })

    const { hiddenCount, containerRef } = useMaxItemsDisplayed(selectedIndexes, maxItemsDisplayed)

    const [searchTerm, setSearchTerm] = useState('')
    const filteredElementsRef = useRef(elementsRef.current)

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndexes,
        getItemProps,
        handleSelect,
        size,
        searchTerm,
        setSearchTerm,
        showSearch,
        allowMultiple: true,
      }),
      [activeIndex, selectedIndexes, getItemProps, handleSelect, size, searchTerm, showSearch],
    )
    return (
      <ComboboxBase
        ref={ref}
        disabled={disabled}
        size={size}
        isOpen={isOpen}
        refs={refs}
        floatingStyles={floatingStyles}
        context={context}
        children={children}
        showSearch={showSearch}
        searchPlaceholder={searchPlaceholder}
        selectedChildren={
          <>
            {selectedChildren?.length === 0 ? (
              <div className={clsx('mui-select-multi-placeholder', styles.placeholder)}>
                {placeholder}
              </div>
            ) : null}

            <div
              ref={containerRef}
              className={clsx('mui-select-multi-chips-wrapper', styles.container, styles[size], {
                [styles.maxItemsDisplayed]: maxItemsDisplayed !== undefined,
              })}
            >
              {selectedChildren.map((child, index) => (
                <Chip
                  size={size}
                  key={index}
                  aria-hidden={index >= selectedChildren.length - hiddenCount}
                  className={clsx({
                    [styles.hiddenChip]: index >= selectedChildren.length - hiddenCount,
                  })}
                  icon={child.icon}
                  onRemove={(event) => {
                    event.stopPropagation()
                    handleSelect(child.index)
                  }}
                  allowMultiple={true}
                >
                  {child?.children}
                </Chip>
              ))}
              <Chip
                size={size}
                key="hidden-count"
                className={clsx({
                  [styles.hiddenChip]: hiddenCount <= 0,
                })}
              >
                +{hiddenCount}
              </Chip>
            </div>
          </>
        }
        referenceProps={getReferenceProps({ onBlur, onFocus })}
        floatingProps={getFloatingProps()}
        elementsRef={filteredElementsRef}
        labelsRef={labelsRef}
        selectContext={selectContext}
        {...props}
      />
    )
  },
)

ComboboxMultiple.displayName = 'ComboboxMultiple'
